import { Box, useTheme } from "@mui/material";
import { useApi } from "@platform/api";
import {
  AIExtractedFieldCard,
  type AIExtractedRow,
} from "@platform/btx-grid-components";
import {
  CellReviewShell,
  FieldCommentCorner,
} from "@platform/btx-plan-designer";
import { schemeAiPalette } from "@platform/design-system";
import { buildFieldLookupKey } from "@platform/document-library";
import { useNotification } from "@platform/hooks";
import {
  cornerTriangleSx,
  useAiFieldLookup,
  type AiFieldData,
  type AiFieldLookupMap,
} from "@platform/plan-grid-core";
import { useState } from "react";

import { useCellReviewCellStyle } from "../shared/cellReviews/useCellReviewCellStyle";

import { deriveCompsCellForRenderer } from "./compsCellReviewIdentity";
import { INCUMBENT_COL_KEY, REQUESTED_COL_KEY } from "./types";

import type { ICellRendererParams } from "ag-grid-community";
import type { CompsGridContext, CompsGridRow } from "./types";

type CompsCellRendererParams = ICellRendererParams<
  CompsGridRow,
  unknown,
  CompsGridContext
>;

/** BTX field id AI Dox assigns to the "Commission" field it extracts from carrier proposals. */
const AI_COMMISSION_FIELD = "commission";

/** Hover target over the top-left AI corner triangle, sized to cover it. */
const AI_HINT_TRIGGER_SIZE = 9;

/**
 * Resolves the AI extraction behind a carrier column's commission cell, keyed
 * by the cell's proposal plan. Requested has no proposal plan, and Inforce is
 * mapped to the incumbent carrier's proposal plan while it displays the
 * inforce commission — a hint there would describe a different value.
 */
const resolveAiCommissionField = (
  params: CompsCellRendererParams,
  aiFieldLookup: AiFieldLookupMap,
): AiFieldData | undefined => {
  const columnKey = params.colDef?.field;
  const row = params.data;
  if (
    !columnKey ||
    columnKey === REQUESTED_COL_KEY ||
    columnKey === INCUMBENT_COL_KEY ||
    row?.rowType !== "commission"
  ) {
    return undefined;
  }
  const proposalPlanId = params.context?.proposalPlanIdByCellKey.get(
    `${columnKey}:${row.rowGroupId}`,
  );
  if (!proposalPlanId) return undefined;
  return aiFieldLookup[
    buildFieldLookupKey(proposalPlanId, AI_COMMISSION_FIELD)
  ];
};

const buildAiRow = (
  coverageLabel: string,
  aiField: AiFieldData,
): AIExtractedRow => ({
  className: aiField.planName ?? "-",
  fieldName: `${coverageLabel} Commission`,
  value: aiField.value === "NOT_FOUND" ? "-" : aiField.value,
  pdfPage: aiField.pageNumber != null ? `Page ${aiField.pageNumber}` : "-",
  pdfPageNumber: aiField.pageNumber ?? 0,
  justification: aiField.justification,
  documentDownloadUrl: aiField.documentDownloadUrl ?? undefined,
  documentName: aiField.documentName ?? undefined,
});

/**
 * AI corner triangle plus the hover card showing the extracted value and its
 * source (plan, page, justification, document link).
 */
const AiCommissionHint = ({
  coverageLabel,
  aiField,
}: {
  coverageLabel: string;
  aiField: AiFieldData;
}): React.JSX.Element => {
  const theme = useTheme();
  const axiosInstance = useApi();
  const { showError } = useNotification();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isOverTrigger, setIsOverTrigger] = useState(false);
  const [isOverCard, setIsOverCard] = useState(false);

  return (
    <>
      <Box
        data-testid="comps-cell-ai-extracted"
        sx={cornerTriangleSx("top-left", schemeAiPalette(theme).extracted)}
      />
      <Box
        data-testid="comps-cell-ai-tooltip-trigger"
        onMouseEnter={(e) => {
          setAnchorEl(e.currentTarget);
          setIsOverTrigger(true);
        }}
        onMouseLeave={() => setIsOverTrigger(false)}
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: AI_HINT_TRIGGER_SIZE,
          height: AI_HINT_TRIGGER_SIZE,
          cursor: "pointer",
          zIndex: 2,
        }}
      />
      {anchorEl !== null && (
        <AIExtractedFieldCard
          row={buildAiRow(coverageLabel, aiField)}
          anchorEl={anchorEl}
          open={isOverTrigger || isOverCard}
          onMouseEnter={() => setIsOverCard(true)}
          onMouseLeave={() => setIsOverCard(false)}
          axiosInstance={axiosInstance}
          onError={(message) => showError(message, false)}
        />
      )}
    </>
  );
};

/**
 * AG Grid cell renderer for Comps carrier columns.
 *
 * Applies the "Needs Review" (yellow) and "Highlight" (blue) background
 * tints, a comment corner indicator, and — on carrier commission cells AI Dox
 * extracted from a proposal document — the AI hint with its source data, then
 * renders the pre-formatted cell value.
 */
export const CompsCellRenderer = (
  params: CompsCellRendererParams,
): React.JSX.Element => {
  const { backgroundColor, comment } = useCellReviewCellStyle(
    deriveCompsCellForRenderer(params),
  );
  const aiFieldLookup = useAiFieldLookup();
  const aiField = resolveAiCommissionField(params, aiFieldLookup);

  return (
    <CellReviewShell
      backgroundColor={backgroundColor}
      overlays={
        <>
          {comment && <FieldCommentCorner comment={comment} />}
          {aiField && params.data && (
            <AiCommissionHint
              coverageLabel={params.data.coverageLabel}
              aiField={aiField}
            />
          )}
        </>
      }
    >
      {params.valueFormatted ?? String(params.value ?? "")}
    </CellReviewShell>
  );
};
